import PropTypes from 'prop-types';
import React, { useState, useRef } from 'react';
import SvgIcon from 'components/SvgIcon/SvgIcon';
import styles from './ResumeUpload.module.scss';
import { Button, Col, Grid, Row } from 'react-bootstrap';

const ResumeUpload = ({
  onUpload,
  uploading,
  uploadError,
  uploadSuccess,
  showReupload,
}) => {
  const supportedFileTypes = ['pdf', 'docx'];
  const acceptTypes = '.pdf,.docx';
  const maxFileSize = 5 * 1024 * 1024; // 5MB in bytes
  const maxFileSizeLabel = '5MB';
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      const fileType = file.name.split('.').pop().toLowerCase();

      // Check file type
      if (!supportedFileTypes.includes(fileType)) {
        setError(
          'Unsupported file type. Only PDF and DOCX (Word) files are supported.'
        );
        return;
      }

      // Check file size
      if (file.size > maxFileSize) {
        setError(
          `File size exceeds the ${maxFileSizeLabel} limit. Please choose a smaller file.`
        );
        return;
      }

      setSelectedFile(file);
      setError(null); // Clear any previous errors

      // Automatically upload the file when selected
      onUpload(file);
    }
  };

  const handleUpload = () => {
    if (selectedFile) {
      setError(null); // Clear any previous errors
      onUpload(selectedFile);
    }
  };

  // Prevent default behavior for all drag events to enable drop functionality
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) {
      setIsDragging(true);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (uploading) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      const fileType = file.name.split('.').pop().toLowerCase();

      // Check file type
      if (!supportedFileTypes.includes(fileType)) {
        setError(
          'Unsupported file type. Only PDF and DOCX (Word) files are supported.'
        );
        return;
      }

      // Check file size
      if (file.size > maxFileSize) {
        setError(
          `File size exceeds the ${maxFileSizeLabel} limit. Please choose a smaller file.`
        );
        return;
      }

      setSelectedFile(file);
      setError(null); // Clear any previous errors

      // Automatically upload the file when dropped
      onUpload(file);
    }
  };

  const triggerFileInput = () => {
    if (fileInputRef.current && !uploading) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className={styles.resumeUploadContainer}>
      <input
        type="file"
        ref={fileInputRef}
        accept={acceptTypes}
        onChange={handleFileChange}
        disabled={uploading}
        className={styles.hiddenInput}
      />

      {!selectedFile && (
        <div
          className={`${styles.dropZone} ${
            isDragging ? styles.dropZoneActive : ''
          }`}
          onClick={triggerFileInput}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <SvgIcon icon="upload" size="32" className={styles.uploadIcon} />
          <div className={styles.uploadText}>
            Click to Upload or Drag & Drop
          </div>
          <div className={styles.description}>
            Upload your Resume in PDF or DOCX (Word) format (max{' '}
            {maxFileSizeLabel})
          </div>
        </div>
      )}

      {error && (
        <div className={styles.errorMessage} style={{ marginTop: '15px' }}>
          <strong>{error}</strong>
        </div>
      )}

      {selectedFile && (
        <Grid className={styles.selectedFileContainer}>
          <Row>
            <Col sm={12}>
              <div
                className={`${styles.selectedFileZone} ${
                  isDragging && !uploading ? styles.dropZoneActive : ''
                } ${uploading ? styles.selectedFileZoneUploading : ''}`}
                onClick={uploading ? undefined : triggerFileInput}
                onDragEnter={uploading ? undefined : handleDragEnter}
                onDragOver={uploading ? undefined : handleDragOver}
                onDragLeave={uploading ? undefined : handleDragLeave}
                onDrop={handleDrop}
                aria-busy={uploading}
              >
                <div className={styles.fileDetails}>
                  <SvgIcon
                    icon="file-o"
                    size="16"
                    className={styles.fileIcon}
                  />
                  <span className={styles.fileName}>{selectedFile.name}</span>
                </div>
                {uploading ? (
                  <div className={styles.uploadingStatus}>
                    <span
                      className={styles.uploadingSpinner}
                      aria-hidden="true"
                    />
                    <span>Uploading…</span>
                  </div>
                ) : (
                  <>
                    <div className={styles.mobileHelpText}>
                      Tap here to change your file
                    </div>
                    <div className={styles.editOverlay}>
                      <SvgIcon
                        icon="pencil"
                        size="18"
                        className={styles.editIcon}
                      />
                      <span>Change file</span>
                    </div>
                  </>
                )}
              </div>

              {uploadSuccess && !uploading && (
                <div className={styles.successMessage}>
                  <strong>Resume processed successfully</strong>
                </div>
              )}

              {uploadError && !uploading && (
                <div className={styles.errorMessage}>
                  <strong>{uploadError}</strong>
                </div>
              )}

              {error && (
                <div className={styles.errorMessage}>
                  <strong>{error}</strong>
                </div>
              )}
            </Col>
          </Row>
          {showReupload && (uploadError || error) ? (
            <Row>
              <Col sm={4} smOffset={4}>
                <div className={styles.uploadButtonContainer}>
                  <Button
                    bsStyle="primary"
                    block
                    className={styles.uploadButton}
                    onClick={handleUpload}
                    disabled={uploading}
                  >
                    {uploading ? 'Uploading...' : 'Re-upload Resume'}
                  </Button>
                </div>
              </Col>
            </Row>
          ) : null}
        </Grid>
      )}
    </div>
  );
};

ResumeUpload.propTypes = {
  onUpload: PropTypes.func.isRequired,
  uploading: PropTypes.bool,
  uploadError: PropTypes.string,
  uploadSuccess: PropTypes.bool,
  showReupload: PropTypes.bool,
};

ResumeUpload.defaultProps = {
  uploading: false,
  uploadError: null,
  uploadSuccess: false,
  showReupload: true,
};

export default ResumeUpload;
